// Help Center content. Each topic is plain text (markdown-ish: blank lines split
// paragraphs, lines starting with "- " are bullets, lines ending with ":" that are
// followed by bullets read as sub-headings). `adminOnly: true` topics are shown
// only to admins (SuiteCRM administration basics). `keywords` boosts search recall.
//
// To add help: append a topic below. Nothing else needs to change.

export const HELP_CATEGORIES = [
  'Getting Started',
  'Clients & Pipeline',
  'Projects',
  'Catalog',
  'Productivity',
  'Finance & Reports',
  'Tools',
  'Administration',
];

export const HELP_TOPICS = [
  // ── Getting Started ───────────────────────────────────────────────
  {
    id: 'welcome',
    title: 'Welcome to Atelier Studio',
    category: 'Getting Started',
    keywords: 'overview intro start begin home dashboard',
    body: `Atelier Studio is a CRM built for interior designers — it takes a project from the first inquiry all the way through rooms, specifications, proposals, invoices, purchase orders and delivery.

The Dashboard is your home base: it shows active projects, pipeline value, open proposals, upcoming meetings and your priority tasks. Use the sidebar on the left to move between modules.

A typical flow:
- A Lead comes in → you qualify it and convert it to a Project.
- Inside the Project you build Rooms, specify Products into each room, then generate Proposals.
- Approved items become Invoices (paid by card, retainer or credit) and Purchase Orders to your vendors.
- The Project Tracker follows every ordered item through to delivery.`,
  },
  {
    id: 'navigation',
    title: 'Navigating the app',
    category: 'Getting Started',
    keywords: 'sidebar menu tabs icons search where find',
    body: `The left sidebar lists every module (Accounts, Leads, Projects, Products, Calendar, Tasks and more). Click one to open its list.

Most list views support searching, sorting and filtering at the top. Click any row to open its detail/edit view; edits save automatically as you type.

The round Support button (headset) at the top-right is available on every page. It opens the Support tracker and "Report a bug or feature". Help (this help center) and About are under the "?" button, also at the top-right.`,
  },

  // ── Clients & Pipeline ────────────────────────────────────────────
  {
    id: 'leads',
    title: 'Leads & the intake pipeline',
    category: 'Clients & Pipeline',
    keywords: 'lead prospect inquiry convert consultation LOA discovery status source attachment meeting',
    body: `Leads are prospective clients before they become a project. Each lead has a status (Form Submission, Discovery Conducted, Consultation Pending, LOA Sent, Converted, Declined) and a lead source.

From a lead you can:
- Schedule a meeting — set a title prefix, date/time and duration; the meeting is named "<prefix> - First Last" and linked to the lead.
- Attach files (the meeting scheduler shows the lead's current attachments live).
- Convert the lead to a Project when they sign on — this creates the Project and carries the client's details across.`,
  },
  {
    id: 'contacts',
    title: 'Contacts',
    category: 'Clients & Pipeline',
    keywords: 'contact person people role client team member co-worker email phone card',
    body: `Contacts are the people you work with. Each contact has a Contact Role — Client, Vendor, Manufacturer, Contractor, Support or Team Member.

The Contacts page groups people by role. Your own team members appear here as "Team Member" contacts, which also link them to their user accounts for @mentions and assignments.`,
  },
  {
    id: 'accounts',
    title: 'Accounts (Clients)',
    category: 'Clients & Pipeline',
    keywords: 'account company client organization type customer billing address',
    body: `Accounts are the companies or households you serve. The account Type (Client, Partner, Reseller, etc.) classifies the relationship.

Accounts tie into Projects: when you convert a lead or create a project, the client account and its primary contact carry through to the project's billing and proposals.`,
  },
  {
    id: 'opportunities',
    title: 'Opportunities',
    category: 'Clients & Pipeline',
    keywords: 'opportunity deal pipeline sales stage amount probability close',
    body: `Opportunities track potential deals through sales stages (Prospecting → Qualification → Proposal/Price Quote → Negotiation → Closed Won/Lost), each with an amount and probability.

The Dashboard's pipeline value sums your open opportunities so you can see what's in play at a glance.`,
  },

  // ── Projects ──────────────────────────────────────────────────────
  {
    id: 'projects',
    title: 'Projects overview',
    category: 'Projects',
    keywords: 'project overview tabs sidebar rooms status client',
    body: `A Project is the hub for one client engagement. Open a project to see its own sidebar of tabs:

- Rooms — the spaces you're designing, each with products and a floor plan.
- Products and Services — every specified item across the project, with a status (Unassigned / Proposed / Invoiced).
- Project Tracker — delivery/shipping status of ordered items.
- Proposals, Invoices, Purchase Orders — the financial documents.
- Retainers/Credits/SRISD — the client's prepaid balances.
- Tasks, Notes, Attachments.

Projects are created by converting a Lead, or directly, and link to a client Account and primary Contact.`,
  },
  {
    id: 'rooms',
    title: 'Rooms & floor plans',
    category: 'Projects',
    keywords: 'room space floorplan floor plan layout drawing image products specify',
    body: `Rooms break a project into spaces (Living Room, Primary Bedroom, etc.). Within a room you specify products, add images and drawings, and lay out a floor plan.

Floor plan / Layout tab:
- Import a PDF floor plan — it's rasterized to an image and shown as the background.
- Drop product markers onto the plan to show where each item goes.

Products specified into a room become "room products", which flow into Proposals, Invoices and Purchase Orders.`,
  },
  {
    id: 'mood-board',
    title: 'Mood Board',
    category: 'Projects',
    keywords: 'mood board moodboard canvas collage swatch material tile texture inspiration pattern image paste clipboard tile pattern repeat source url link brand export png',
    body: `Mood Boards are a Canva-style canvas on each project (tab under the project header, next to Rooms) for pulling together inspiration imagery, material swatches and notes. Boards save as you edit, and you can keep several per project — switch between them from the board list.

What you can place:
- Images — drag-and-drop files, paste a screenshot or copied web image with Ctrl/⌘+V, or pull from the project's own uploads.
- Swatches — a solid colour tile, a labeled material/texture tile, or a tiled pattern that fills an area.
- Text — titles, captions and notes with font, weight, italics and alignment.

Manipulating items:
- Click to select, drag to move. The corner handle resizes; the top handle rotates.
- Toolbar actions bring an item forward, send it back, or delete it. Delete/Backspace removes a selected item; Escape clears selection.
- Images: Remove BG runs an AI cutout for product-shot style collages, and To Swatch converts an image into a swatch so it can be labeled or tiled.

Material swatches and tiling:
- Click Swatch for a solid colour tile, or Material to pick an image/texture.
- Add a label and a border radius if you want.
- ⊞ Tile captures the swatch's current size as the repeat unit — then resize the item's box with the corner handle and the swatch fills the whole area with repeated tiles. Use ▦− / ▦+ to shrink or enlarge the tile unit. Clicking ⊞ Tile again turns tiling off.

Source URLs for images:
- When you right-click an image on a webpage and your browser puts a wrapping link on the clipboard, the paste auto-captures that URL — a small ↗ badge appears in the top-right of the item on hover and opens the source page in a new tab.
- Chrome's plain "Copy image" only puts the image CDN URL on the clipboard, not the page URL — in that case paste the page's address into the Source URL input on the item's context strip, and the badge wires up.
- Install the Atelier Studio Capture Chrome extension and right-click → Copy image for Mood Board (with page URL): the extension packages the image AND the page URL on the clipboard so the ↗ badge is attached automatically.

Export:
- Export PNG renders the whole board (background + items + swatches + tiled patterns + text + labels) to a 1800-wide PNG you can download, email or drop into a presentation.`,
  },
  {
    id: 'products-services',
    title: 'Products and Services',
    category: 'Projects',
    keywords: 'products services items specify status proposed invoiced charges create proposal thumbnail',
    body: `The Products and Services tab lists every item specified across the project's rooms, plus any manual Services & Charges (labor, consulting, storage). Each row shows a thumbnail, ID, item, SKU, quantity, price and status.

Status reflects where an item is in its lifecycle: Unassigned → Proposed (on a proposal) → Invoiced.

To create a proposal: tick the products/charges you want and click "Create Proposal from Selected", then name it. The selected items flip to Proposed.`,
  },
  {
    id: 'proposals',
    title: 'Proposals',
    category: 'Projects',
    keywords: 'proposal quote client pdf line items generate stage confirmed present price',
    body: `Proposals (quotes) present selected items to the client for approval. Create one from the Products and Services tab (see that topic) — proposals are always built from selected room products/charges.

A proposal has a stage (Draft, Negotiation, Delivered, Confirmed, etc.), line items with quantities and prices, tax computed from the client's location, and a total. Open a proposal to refine line items and generate a PDF to send.

When a proposal is accepted, convert its items into Invoices.`,
  },
  {
    id: 'invoices',
    title: 'Invoices & payments',
    category: 'Projects',
    keywords: 'invoice payment paid card stripe retainer credit srisd due status ledger',
    body: `Invoices bill the client for confirmed items. Each has a status (Draft, Unpaid, Paid, Cancelled), line items, tax and a due date.

Recording payments:
- Card — a Stripe payment link/checkout; when paid, the invoice is marked Paid automatically (via the Stripe Connect webhook).
- Retainer / Credit / SRISD — draws down the client's prepaid balance for the project.
- Manual (check/cash) — recorded in the payment ledger.

An invoice counts as paid when its status is Paid or recorded payments cover the total.`,
  },
  {
    id: 'purchase-orders',
    title: 'Purchase Orders',
    category: 'Projects',
    keywords: 'purchase order PO vendor procurement order line items generate packing slip',
    body: `Purchase Orders order the specified goods from your vendors. Generate POs from a project's included room products — items are grouped by vendor and shipping address, one PO per group.

A PO has a status (Draft, Pending Approval, Approved, Ordered, Received, …), line items with cost and quantity, carrier/tracking fields, and can hold attachments like packing slips.`,
  },
  {
    id: 'project-tracker',
    title: 'Project Tracker',
    category: 'Projects',
    keywords: 'tracker delivery shipping tracking carrier status estimated ship date packing slip',
    body: `The Project Tracker is a delivery-focused view over all of a project's purchase-order line items. It shows each ordered item with its estimated ship date, tracking number, carrier, delivery/status notes and packing-slip files.

Editing tracking here writes straight back to the underlying purchase orders — it's the same data, presented for follow-up.`,
  },
  {
    id: 'rcs',
    title: 'Retainers, Credits & SRISD',
    category: 'Projects',
    keywords: 'retainer credit srisd balance deposit account funds prepaid draw down stripe',
    body: `Each project can hold prepaid client balances in three accounts: Retainer, Credit and SRISD. The balance for each is the sum of its completed transactions.

- Deposits add funds (recorded manually, or paid by the client via Stripe).
- Applications draw funds down when an invoice or proposal is paid using that method.

Use these to hold a design retainer or store credit and apply it against invoices over the life of the project.`,
  },

  // ── Catalog ───────────────────────────────────────────────────────
  {
    id: 'catalog',
    title: 'Product catalog',
    category: 'Catalog',
    keywords: 'product catalog item image sku price cost vendor manufacturer edit capture',
    body: `The Products module is your master catalog. Each product has a client-facing name, SKU, category, vendor/manufacturer, dimensions, cost and price, plus one or more images.

Open a product to edit it — the image appears at the top of the edit page. Products you add here can be specified into any room; capturing from the web (see Web Capture) adds products automatically.`,
  },
  {
    id: 'assemblies',
    title: 'Product assemblies',
    category: 'Catalog',
    keywords: 'assembly bundle kit components rolled up package set',
    body: `An assembly is a product made of other products — e.g. a "Primary Bedroom Package" of a bed, nightstands and lamps. Its cost and price roll up from its components.

Assemblies specify as a single line but represent the whole set, which is handy for packaged offerings.`,
  },
  {
    id: 'vendors',
    title: 'Vendors (Businesses)',
    category: 'Catalog',
    keywords: 'vendor business manufacturer distributor supplier showroom workroom trade account',
    body: `The Businesses module holds your vendors, manufacturers, showrooms and workrooms — your trade accounts. Each has a type and contact details.

Products carry a vendor, and Purchase Orders are grouped and issued per vendor.`,
  },

  // ── Productivity ──────────────────────────────────────────────────
  {
    id: 'calendar',
    title: 'Calendar & meetings',
    category: 'Productivity',
    keywords: 'calendar meeting event schedule call attendee reminder lead attachment',
    body: `The Calendar shows meetings and calls. Create a meeting with a title, date/time, duration, location and attendees (team members and contacts).

Meetings can be linked to a Lead, Account or other record. When a meeting is tied to a lead, the edit window shows that lead's current attachments so you can open them during the call.`,
  },
  {
    id: 'tasks',
    title: 'Tasks',
    category: 'Productivity',
    keywords: 'task todo status priority assignee subtask activity mention project due',
    body: `Tasks track work items with a status (Not Started, In Progress, Info Needed, Completed, …), priority, assignee and due date. Tasks can belong to a project and have subtasks.

Open a task to see its detail: description, subtasks and an Activity thread where you can reply and @mention team members. Mentioning a teammate notifies them.`,
  },
  {
    id: 'cases',
    title: 'Cases',
    category: 'Productivity',
    keywords: 'case support issue ticket customer help resolve',
    body: `Cases capture client-facing issues or requests that need tracking to resolution — separate from internal Tasks. Assign, prioritize and update them as you work through each one.`,
  },

  // ── Finance & Reports ─────────────────────────────────────────────
  {
    id: 'accounting',
    title: 'Accounting reports',
    category: 'Finance & Reports',
    keywords: 'accounting report sales tax collected paid snapshot financial product service labor',
    body: `Accounting is a report engine. Choose a report type, set its parameters (date range, project, etc.) and run it. You can save a snapshot, which freezes the numbers as of that moment.

The Sales Tax report breaks tax down by Product / Service / Labor and shows collected vs paid.`,
  },

  // ── Tools ─────────────────────────────────────────────────────────
  {
    id: 'web-capture',
    title: 'Web Capture (browser extension)',
    category: 'Tools',
    keywords: 'chrome extension capture clip product image website vendor sidebar scrape',
    body: `The Atelier Studio Capture browser extension clips products and images from any website straight into your product library and, optionally, a project room.

How it works:
- Open the extension's side panel; it connects to your workspace automatically.
- On a product page, drag to crop an image (or use the auto-scanned photos). The extension auto-detects the name, price, SKU, brand and dimensions.
- Review the details, pick a category and (optionally) a project + room, then Save.`,
  },
  {
    id: 'support',
    title: 'Support & reporting a bug',
    category: 'Tools',
    keywords: 'support bug feature report help tracker screenshot attachment status about question mark',
    body: `The Support button (headset, top-right) is your feedback hub:
- Report a bug or feature — describe the issue, capture and annotate a screenshot, and attach files. It's filed to the Support tracker.
- Support — the tracker list of all reports; open one to follow its status, read developer responses, and attach or open files.

Help and About now live under the "?" button, also at the top-right:
- Help — this help center.
- About — the app version.`,
  },
  {
    id: 'account-settings',
    title: 'Account settings & profile',
    category: 'Tools',
    keywords: 'settings profile password company name preferences account',
    body: `Open Account Settings (top-right) to manage your profile — your name, company name and password — and your preferences.`,
  },

  // ── Administration (admins only) ──────────────────────────────────
  {
    id: 'admin-users',
    title: 'Adding & managing users',
    category: 'Administration',
    adminOnly: true,
    keywords: 'admin add user create account team member login suitecrm employee password',
    body: `Adding users is done in the classic SuiteCRM admin area (see "The classic SuiteCRM admin panel").

To add a user:
- Open the SuiteCRM admin panel and go to User Management.
- Click "Create New User". Enter the user name, first/last name and email on the User Profile tab.
- On the Password tab, set a temporary password (or have the system email one).
- Set the user Type — "Regular" for staff, "Administrator" for full access — and save.

The new person can then sign in with those credentials. To also surface them for @mentions and assignments in Atelier Studio, add a matching Contact with the role "Team Member" (same name/email).`,
  },
  {
    id: 'admin-roles',
    title: 'Roles & permissions',
    category: 'Administration',
    adminOnly: true,
    keywords: 'admin role permission acl access module security group restrict',
    body: `Roles control which modules and actions each user can access. Manage them in the SuiteCRM admin area under Role Management.

- Create a role, then set per-module permissions (view/edit/delete/etc.).
- Assign users to the role.
- Security Groups can further scope which records a user sees.

Administrators bypass role restrictions and can see everything.`,
  },
  {
    id: 'admin-suitecrm',
    title: 'The classic SuiteCRM admin panel',
    category: 'Administration',
    adminOnly: true,
    keywords: 'admin suitecrm classic legacy panel settings system users roles studio scheduler',
    body: `Atelier Studio runs on SuiteCRM. Deeper administration — users, roles, email settings, schedulers and system configuration — lives in the classic SuiteCRM admin panel.

Reach it by logging into the legacy SuiteCRM interface as an administrator and opening Admin (top-right). From there you have User Management, Role Management, Email settings, Schedulers and System Settings.

Day-to-day design work happens in Atelier Studio; use the classic admin only for these system-level tasks.`,
  },
  {
    id: 'admin-billing',
    title: 'Subscription & billing',
    category: 'Administration',
    adminOnly: true,
    keywords: 'admin billing subscription plan trial upgrade stripe payment seats users limit paywall',
    body: `Your workspace runs on a subscription. During a free trial there are limits on records and users; the About/Account area shows the current plan and trial status.

To remove trial limits, upgrade the subscription (Stripe checkout). Billing is managed from the account/developer settings for administrators.`,
  },
];
